import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Target, Pencil, Check, X, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { fmtInt as _fmtInt } from "@/lib/format";

const MONTH_NAMES = [
  "Januar", "Februar", "März", "April", "Nie", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "0";
  return _fmtInt(n);
}

function progressColor(percent) {
  if (percent >= 100) return "bg-emerald-500";
  if (percent >= 75) return "bg-emerald-400";
  if (percent >= 50) return "bg-amber-400";
  if (percent >= 25) return "bg-amber-500";
  return "bg-prime";
}

function progressBadge(percent) {
  if (percent >= 100) return { label: "Ziel raggiunto", cls: "bg-emerald-100 text-emerald-700" };
  if (percent >= 75) return { label: "Quasi raggiunto", cls: "bg-emerald-50 text-emerald-700" };
  if (percent >= 50) return { label: "In linea", cls: "bg-amber-100 text-amber-700" };
  if (percent >= 25) return { label: "In ritardo", cls: "bg-amber-100 text-amber-800" };
  return { label: "Unter Ziel", cls: "bg-rose-100 text-prime" };
}

export default function GoalBarometer() {
  const [goal, setGoal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [targetInput, setTargetInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/commissions/my-goal");
      setGoal(r.data);
      setTargetInput(String(r.data.target_amount || ""));
    } catch (e) { /* */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/commissions/my-goal", { target_amount: Number(targetInput || 0) });
      setGoal(r.data);
      setEditing(false);
      toast.success("Ziel aggiornato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  if (!goal) {
    return (
      <div className="prime-card p-6">
        <div className="text-sm text-muted-foreground">Lädt barometro…</div>
      </div>
    );
  }

  const percent = goal.percent || 0;
  const monthLabel = (() => {
    const [y, m] = goal.year_month.split("-");
    return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
  })();
  const badge = progressBadge(percent);
  const barColor = progressColor(percent);

  return (
    <div className="prime-card p-4" data-testid="goal-barometer">
      {/* Compact single-row layout: header · KPIs · bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Target className="w-4 h-4 text-prime shrink-0" />
          <h2 className="font-display font-extrabold text-base tracking-tight whitespace-nowrap">
            Provisions-Barometer
          </h2>
          <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            · {monthLabel}
          </span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${badge.cls} ml-1`} data-testid="goal-badge">
            {badge.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Inline KPIs */}
          <div className="hidden md:flex items-baseline gap-4 font-mono text-xs text-muted-foreground">
            <span><span className="label-uppercase text-[9px] mr-1">Ziel</span><span className="font-bold text-foreground text-sm">CHF {fmtMoney(goal.target_amount)}</span></span>
            <span><span className="label-uppercase text-[9px] mr-1 text-prime">Verdient</span><span className="font-bold text-prime text-sm">CHF {fmtMoney(goal.earned_total)}</span></span>
            <span><span className="label-uppercase text-[9px] mr-1 text-emerald-700 dark:text-emerald-400">Kassiert</span><span className="font-bold text-emerald-700 dark:text-emerald-400 text-sm">CHF {fmtMoney(goal.earned_paid)}</span></span>
            <span><span className="label-uppercase text-[9px] mr-1">Fehlt</span><span className={`font-bold text-sm ${goal.delta <= 0 ? "text-emerald-600" : "text-foreground"}`}>{goal.delta <= 0 ? "✓" : `CHF ${fmtMoney(goal.delta)}`}</span></span>
          </div>
          {!editing && (
            <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={() => setEditing(true)} data-testid="edit-goal-btn">
              <Pencil className="w-3 h-3" /> Ziel
            </Button>
          )}
        </div>
      </div>

      {/* Target editor */}
      {editing && (
        <div className="mb-3 flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Ziel mensile (CHF)</label>
            <Input
              type="number"
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              placeholder="5000"
              className="h-9 mt-1"
              data-testid="goal-target-input"
              autoFocus
            />
          </div>
          <Button onClick={save} disabled={saving} size="sm" className="gap-1 bg-prime text-white hover:bg-prime/90 h-9" data-testid="goal-save-btn">
            <Check className="w-3.5 h-3.5" /> Speichern
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setEditing(false); setTargetInput(String(goal.target_amount || "")); }} className="gap-1 h-9" data-testid="goal-cancel-btn">
            <X className="w-3.5 h-3.5" /> Abbrechen
          </Button>
        </div>
      )}

      {/* Mobile KPIs (visible only when md is hidden) */}
      <div className="grid grid-cols-2 gap-2 mb-3 md:hidden font-mono text-xs">
        <div><span className="label-uppercase text-[9px]">Ziel </span><span className="font-bold">CHF {fmtMoney(goal.target_amount)}</span></div>
        <div><span className="label-uppercase text-[9px] text-prime">Verdient </span><span className="font-bold text-prime">CHF {fmtMoney(goal.earned_total)}</span></div>
        <div><span className="label-uppercase text-[9px] text-emerald-700">Kassiert </span><span className="font-bold text-emerald-700">CHF {fmtMoney(goal.earned_paid)}</span></div>
        <div><span className="label-uppercase text-[9px]">Fehlt </span><span className={`font-bold ${goal.delta <= 0 ? "text-emerald-600" : ""}`}>{goal.delta <= 0 ? "✓" : `CHF ${fmtMoney(goal.delta)}`}</span></div>
      </div>

      {/* Slim bar with floating percent badge */}
      <div className="relative" data-testid="goal-bar">
        <div className="h-3 bg-secondary rounded-full overflow-hidden border border-border relative">
          <div
            className={`h-full transition-[width] duration-700 ease-out ${barColor}`}
            style={{ width: `${Math.min(percent, 100)}%` }}
          />
          {[25, 50, 75].map((m) => (
            <div
              key={m}
              className="absolute top-0 bottom-0 border-l border-border/40"
              style={{ left: `${m}%` }}
            />
          ))}
        </div>
        <div
          className="absolute -top-1 transition-[left] duration-700 ease-out pointer-events-none"
          style={{ left: `${Math.min(Math.max(percent, 0), 100)}%`, transform: "translateX(-50%)" }}
        >
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-foreground text-background text-[9px] font-bold whitespace-nowrap shadow">
            <TrendingUp className="w-2.5 h-2.5" /> {percent}%
          </span>
        </div>
      </div>

      {goal.policies_count > 0 && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <Sparkles className="w-3 h-3 text-prime shrink-0" />
          <span>{goal.policies_count} Polic{goal.policies_count === 1 ? "a" : "e"} diesen Monat abgeschlossen.</span>
        </div>
      )}
    </div>
  );
}
